import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import get from 'lodash/get';
import isNil from 'lodash/isNil';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar } from '@fortawesome/sharp-solid-svg-icons/faStar'
import { faAward } from '@fortawesome/sharp-solid-svg-icons/faAward'
import { faThumbsUp } from '@fortawesome/sharp-solid-svg-icons/faThumbsUp'

import { faDownload } from '@fortawesome/pro-solid-svg-icons/faDownload'

import AssetPath from '../../../../../../../lib/modules/asset_path';
import { t } from '../../../helpers/t';
import { SecondaryCTA } from '../../Buttons';
import { MagnifyingGlassPlusIcon } from '@heroicons/react/20/solid';
import classNames from 'classnames';

export default function ListingPhotoMosaic() {
  const { __CLIENT_HYDRATED__ } = useSelector((state) => state.layout);
  const {
    _meta,
    id,
    listing_photos,
    open_drafts,
    neighborhood,
    virtual_tour_url,
    title,
    country,
    city,
    cached_photo_medium,
    cached_photo_large
  } = useSelector((state) => state.listing);

  const [virtualTourUrl, setVirtualTourUrl] = useState(null);
  const [listingImageTitle, setListingImageTitle] = useState("");

  const photos = virtual_tour_url ? [{ dummy: 'video tour' }].concat(listing_photos) : listing_photos;
  const placeholder = AssetPath.get('/assets/images/stripes-light.png');

  let nCols = 3;

  if (photos.length > 3) {
    nCols = 4;
  }

  useEffect(() => {
    const draftVirtualTourUrl = get(open_drafts, '[0].virtual_tour_url');
    const tourUrl = draftVirtualTourUrl || virtual_tour_url;
    setVirtualTourUrl(tourUrl);

    if (typeof window !== 'undefined' && window.document && tourUrl && tourUrl === virtual_tour_url) {
      document.addEventListener('DOMContentLoaded', function() {
        analytics.track('3d visit displayed')
      });
    }
  }, []);

  useEffect(() => {
    if (__CLIENT_HYDRATED__) {
      setListingImageTitle(
        neighborhood
          ? t('listings.show.listingImage.altWithNeighborhood', { title, neighborhood, city, country })
          : t('listings.show.listingImage.altWithoutNeighborhood', { title, city, country })
      );
    }
  }, [__CLIENT_HYDRATED__]);

  return (
    <div className="listing-photo-header h-80 md:h-[450px] lg:h-[500px]">
      <div className={classNames(
        'photo-header overflow-hidden grid grid-cols-2 grid-rows-2 gap-1 md:rounded-lg',
        nCols < 4 ? 'md:grid-cols-3 lg:grid-cols-3' : 'md:grid-cols-4 lg:grid-cols-4'
      )}>
        <div className={classNames(virtualTourUrl ? 'is-video' : '', 'listing-mosaic-image row-span-2 col-span-3')}
             title={listingImageTitle} data-ng-click="openCarousel($event)" data-photo-index="0">
          {
            virtualTourUrl && (
              <>
                {virtualTourUrl.match(/matterport/)
                  ? (<iframe className="matterport-image border-0" id="showcase_iframe" width="100%" height="100%" src={`${virtualTourUrl}&nt=1&brand=0&gt=0&mls=2&f=0&title=0`}/>)
                  : (virtualTourUrl.match(/(?:https?:\/\/)?(?:www\.)?youtu\.?be(?:\.com)?\/?.*(?:watch|embed)?(?:.*v=|v\/|\/)([\w\-_]+)\&?/)
                      ? (<iframe className="video-image border-0" id="showcase_iframe" width="100%" height="100%" src={`https://www.youtube.com/embed/${virtualTourUrl.match(/(?:https?:\/\/)?(?:www\.)?youtu\.?be(?:\.com)?\/?.*(?:watch|embed)?(?:.*v=|v\/|\/)([\w\-_]+)\&?/)[1]}`}/>)
                      : (<iframe className="video-image border-0" id="showcase_iframe" width="100%" height="100%" src={virtualTourUrl}/>)
                  )}
              </>
            )
          }
        </div>

        <div className="listing-mosaic-image hidden md:block" title={listingImageTitle} data-ng-click="openCarousel($event)" data-photo-index={isNil(virtualTourUrl) ? 1 : 0} />
        <div className="listing-mosaic-image hidden md:block" title={listingImageTitle} data-ng-click="openCarousel($event)" data-photo-index={isNil(virtualTourUrl) ? 2 : 1} />

        <div
        dangerouslySetInnerHTML = {
          { __html:  `
              <style>
                div.photo-header .listing-mosaic-image:nth-child(2) { background-image: url('${encodeURI(photos[1] && photos[1].medium_url || placeholder).replace(/#/g, '%23')}'); }
                div.photo-header .listing-mosaic-image:nth-child(3) { background-image: url('${encodeURI(photos[2] && photos[2].medium_url || placeholder).replace(/#/g, '%23')}'); }
                div.photo-header .listing-mosaic-image:nth-child(4) { background-image: url('${encodeURI(photos[3] && photos[3].medium_url || placeholder).replace(/#/g, '%23')}'); }
                div.photo-header .listing-mosaic-image:nth-child(5) { background-image: url('${encodeURI(photos[4] && photos[4].medium_url || placeholder).replace(/#/g, '%23')}'); }
                ${!virtualTourUrl ? `
                @media (max-width: 768px) { .listing-mosaic-image:nth-child(1){ background-image: url('${encodeURI(cached_photo_medium).replace(/#/g, '%23')}'); } }
                @media (min-width: 768px) { .listing-mosaic-image:nth-child(1) { background-image: url('${encodeURI(cached_photo_large).replace(/#/g, '%23')}'); } }
              </style>
            ` : ''}
            `
          }
        }
      >
        </div>

        <div className="absolute right-2 bottom-2 flex items-center">
          {/* Favorite button */}
          <div
            className="favorite-tag md:hidden"
            data-ng-cloak
            data-sf-favorite-btn
            data-listing-id={id}
            data-listing="listing"
            data-separate-buttons="true"
            data-event-tracking-type="'page'"
            data-event-tracking-on-favorite="true"/>

          {/* See more button */}
          {photos.length > 1 && (
            <SecondaryCTA type="neutral" icon={<MagnifyingGlassPlusIcon width={18}/>} data-ng-click="openCarousel($event)">
              {t('listings.show.sections.photos.showMore')}
            </SecondaryCTA>
          )}

          {/* Admin download button */}
          {_meta.current_user && _meta.current_user.is_admin && (
            <SecondaryCTA type="neutral" icon={<FontAwesomeIcon icon={faDownload} aria-hidden />} data-ng-click="downloadImages()">
              Download photos
            </SecondaryCTA>
          )}
        </div>
      </div>
    </div>
  );
}
